import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import UnifiedTimeline from "../Timeline/UnifiedTimeline";
import { useGetActivityFeedQuery } from "../../../store/activity/activityApi";
import { groupTimelineByDate, normalizeTimelineItems } from "../../../utils/timeline";
import { isSafeActionUrl } from "../../../utils/safeActionUrl";

export const CATEGORY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "investment", label: "Investments" },
  { value: "marketplace", label: "Marketplace" },
  { value: "real_estate", label: "Real Estate" },
  { value: "kyc", label: "KYC" },
  { value: "notification", label: "Notifications" },
];

export function ActivityFeedSkeleton() {
  return <UnifiedTimeline items={[]} loading />;
}

export function ActivityFeedEmpty({ message = "No recent activity yet." }) {
  return <UnifiedTimeline items={[]} emptyMessage={message} />;
}

export function ActivityFeedItem({ item, compact = false, onActivate }) {
  const url = item?.action_url || item?.metadata?.action_url;
  const clickable = isSafeActionUrl(url);

  return (
    <li
      data-activity-id={item?.id}
      role="listitem"
      tabIndex={clickable ? 0 : -1}
      className="mb-0"
      style={{ cursor: clickable ? "pointer" : "default" }}
      onClick={() => clickable && onActivate?.(item)}
      onKeyDown={(e) => {
        if (!clickable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate?.(item);
        }
      }}
    >
      <UnifiedTimeline items={[item]} compact={compact} showActor groupByDate={false} />
    </li>
  );
}

/**
 * Cross-module activity feed. Reuses UnifiedTimeline for rendering.
 */
export default function ActivityFeed({
  guard = "investor",
  perPage = 10,
  showFilters = true,
  showPagination = true,
  groupByDate = true,
  compact = false,
  className = "",
  emptyMessage = "No recent activity yet.",
}) {
  const navigate = useNavigate();
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, isError, error, refetch } = useGetActivityFeedQuery({
    guard,
    page,
    per_page: perPage,
    category: category === "all" ? "" : category,
  });

  const items = useMemo(() => normalizeTimelineItems(data?.items || []), [data?.items]);
  const meta = data?.meta || {};

  const handleItemActivate = (item) => {
    const url = item.action_url || item.metadata?.action_url;
    if (isSafeActionUrl(url)) {
      navigate(url);
    }
  };

  return (
    <div className={`activity-feed ${className}`.trim()}>
      {showFilters ? (
        <div className="d-flex flex-wrap gap-2 mb-3" role="tablist" aria-label="Activity categories">
          {CATEGORY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="tab"
              aria-selected={category === opt.value}
              className="btn btn-sm"
              style={{
                borderRadius: 10,
                border: "1px solid #e0e0e0",
                background: category === opt.value ? "var(--color-primary)" : "#fff",
                color: category === opt.value ? "#fff" : "#383838",
                fontWeight: 600,
              }}
              onClick={() => {
                setCategory(opt.value);
                setPage(1);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}

      <ActivityFeedBody
        items={items}
        loading={isLoading || isFetching}
        error={isError ? error?.data?.message || "Could not load activity." : null}
        groupByDate={groupByDate}
        compact={compact}
        emptyMessage={emptyMessage}
        onRetry={refetch}
        onActivate={handleItemActivate}
      />

      {showPagination && meta.last_page > 1 ? (
        <div className="d-flex justify-content-between align-items-center mt-3 gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={page <= 1 || isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="small text-muted">
            Page {meta.current_page || page} of {meta.last_page}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={page >= (meta.last_page || 1) || isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ActivityFeedBody({
  items,
  loading,
  error,
  groupByDate,
  compact,
  emptyMessage,
  onRetry,
  onActivate,
}) {
  if (error) {
    return (
      <div className="py-3" role="alert">
        <p className="small text-danger mb-2">{error}</p>
        {onRetry ? (
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={onRetry}>
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  if (loading) {
    return <ActivityFeedSkeleton />;
  }

  if (!items.length) {
    return <ActivityFeedEmpty message={emptyMessage} />;
  }

  const groups = groupByDate
    ? groupTimelineByDate(items)
    : [{ key: "all", label: null, items }];

  return (
    <div role="list">
      {groups.map((group) => (
        <div key={group.key} className="mb-3">
          {group.label ? (
            <p
              className="small fw-semibold text-muted text-uppercase mb-2"
              style={{ letterSpacing: "0.04em" }}
            >
              {group.label}
            </p>
          ) : null}
          <ul className="list-unstyled mb-0">
            {group.items.map((item) => (
              <ActivityFeedItem
                key={item.id}
                item={item}
                compact={compact}
                onActivate={onActivate}
              />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
